import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, X, Eye, EyeOff, Wand2 } from 'lucide-react';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';
import { showAlert } from '@/components/AppDialog';
import { clearUsersCache } from '@/utils/adminUsers';
import { BRAND } from '@/brand';

const IS_CORP = BRAND === 'corporate';
const ACCENT = IS_CORP ? '#E8890B' : '#1976D2';

// Admin action: create a client account (Firebase Auth login + profile) from the panel.
export default function NewClientButton() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', password: '', country: '' });

  const set = (k: string, v: string) => setF(prev => ({ ...prev, [k]: v }));

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$';
    let p = '';
    for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    set('password', p);
    setShow(true);
  };

  const submit = async () => {
    setError('');
    if (!f.email.trim()) { setError('Email is required.'); return; }
    if (f.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setSaving(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/create-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ...f, brand: BRAND }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || (json && json.success === false)) throw new Error(json?.error || `Could not create the account (${res.status})`);
      clearUsersCache();
      setOpen(false);
      setF({ firstName: '', lastName: '', email: '', password: '', country: '' });
      await showAlert('Client account created.', { variant: 'success' });
      if (json?.uid) navigate(`/admin/user/${json.uid}`);
    } catch (e: any) {
      setError(e.message || 'Could not create the account.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-white font-bold text-xs px-3.5 py-2.5 rounded-xl whitespace-nowrap hover:-translate-y-0.5 transition-transform"
        style={{ background: ACCENT, boxShadow: '0 10px 22px -14px rgba(0,0,0,.5)' }}
      >
        <UserPlus className="w-4 h-4" /> New client
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" onClick={() => !saving && setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-black text-slate-900">Create a client account</h3>
              <button onClick={() => !saving && setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-5">Creates a real login and profile. Share the password with the client — they can change it later.</p>

            {error && <div className="mb-4 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold text-slate-600 mb-1.5">First name</label><input className={inputCls} value={f.firstName} onChange={e => set('firstName', e.target.value)} /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1.5">Last name</label><input className={inputCls} value={f.lastName} onChange={e => set('lastName', e.target.value)} /></div>
              </div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1.5">Email<span className="text-red-500">*</span></label><input type="email" className={inputCls} value={f.email} onChange={e => set('email', e.target.value)} placeholder="client@company.com" /></div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold text-slate-600">Temporary password<span className="text-red-500">*</span></label>
                  <button type="button" onClick={generatePassword} className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: ACCENT }}><Wand2 className="w-3 h-3" /> Generate</button>
                </div>
                <div className="relative">
                  <input type={show ? 'text' : 'password'} className={inputCls + ' pr-10'} value={f.password} onChange={e => set('password', e.target.value)} placeholder="At least 8 characters" />
                  <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
              </div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1.5">Country <span className="text-slate-400 font-normal">(optional)</span></label><input className={inputCls} value={f.country} onChange={e => set('country', e.target.value)} placeholder="United Arab Emirates" /></div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setOpen(false)} disabled={saving} className="flex-1 rounded-xl border border-slate-300 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
              <button onClick={submit} disabled={saving} className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-60" style={{ background: ACCENT }}>{saving ? 'Creating…' : 'Create account'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
